import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import MaterialTable from "material-table";
import { useTranslation } from "react-i18next";
import { getColumns, getLocalization } from "util/table-utils";
import { mapTableData } from "util/helpers";
import {
  loadFirstPageRoutes,
  loadNextPageRoutes,
  undoRoute,
  updateRoutePositions
} from "redux/slices/routeSlice";
import arrowpanel from "../../assets/img/arrow-panel.svg";
import TableExpansionPanel from "components/Routes/TableExpansionPanel";
import DarkLayout from "components/Shared/DarkLayout";
import Navbar from "components/Navbar";
import ToursNavbar from "components/Masterbar/TourBar";
import { selectUser } from "redux/slices/userSlice";
import { sortingType } from "util/sortingType";
import ReorderStopsModal from "components/Routes/ReorderStopsModal";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import DriverDeviceInfoModal from "components/DriverDeviceInfoModal";

const GenericRoutesList = ({
  routeType,
  columnsConfig,
  columnsWithPDF,
  mapPath,
  features = {},
  confirm,
  selector,
  paginationSelector,
  loadAction,
  filterParams,
  renderFilters
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const tableRef = useRef();
  const { t } = useTranslation();
  const routes = useSelector(selector);
  const pagination = useSelector(paginationSelector);
  const user = useSelector(selectUser);
  const [tabledata, settableData] = useState(routes);
  const loading = useSelector((state) => state.routes.loading);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deviceInfoModalOpen, setDeviceInfoModalOpen] = useState(false);
  const [selectedDeviceInfo, setSelectedDeviceInfo] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openReorderModal = params.get("openReorderModal") === "true";
    const routeId = params.get("routeId");
    if (openReorderModal && routeId) {
      setSelectedRouteId(Number(routeId));
      setIsModalOpen(true);

      const newSearch = new URLSearchParams(location.search);
      newSearch.delete("openReorderModal");
      newSearch.delete("routeId");
      history.replace({
        pathname: location.pathname,
        search: newSearch.toString(),
      });
    }
  }, [location.search, history]);

  const myDivToFocus = useMemo(
    () =>
      Array(tabledata.length)
        .fill(0)
        .map((i) => React.createRef()),
    [tabledata.length]
  );

  const filterParamsRef = useRef(null);
  const currentFilterParamsRef = useRef(filterParams);
  currentFilterParamsRef.current = filterParams;

  const loadFirstPage = useCallback((params) => {
    const finalParams = params !== undefined ? params : (currentFilterParamsRef.current || {});
    dispatch(loadFirstPageRoutes(loadAction, 20, 'id', finalParams));
  }, [dispatch, loadAction]);

  const loadNextPage = useCallback(() => {
    if (pagination.hasNextPage && !pagination.isLoadingMore) {
      dispatch(loadNextPageRoutes(loadAction, 20, currentFilterParamsRef.current || {}));
    }
  }, [dispatch, pagination, loadAction]);

  useInfiniteScroll(loadNextPage, pagination.hasNextPage, pagination.isLoadingMore);

  const filterParamsString = useMemo(() => JSON.stringify(filterParams || {}), [filterParams]);
  const hasInitialLoadRef = useRef(false);

  useEffect(() => {
    if (!loading && !hasInitialLoadRef.current) {
      loadFirstPage(currentFilterParamsRef.current);
      hasInitialLoadRef.current = true;
      filterParamsRef.current = filterParamsString;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (hasInitialLoadRef.current && filterParamsString !== filterParamsRef.current && !loading) {
      loadFirstPage(currentFilterParamsRef.current);
      filterParamsRef.current = filterParamsString;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParamsString, loading]);

  useEffect(() => {
    settableData(routes);
  }, [routes]);

  const scroll = (scrollOffset, rowData) => {
    myDivToFocus[rowData.tableData.id].current.scrollLeft += scrollOffset;
  };

  const markFavourite = (e, rowData) => {
    let favourites = localStorage.getItem("current-tours-favourites");
    favourites = favourites ? favourites.split(",") : [];

    if (rowData.is_favourite) {
      favourites = favourites.filter((f) => parseInt(f, 10) !== rowData.id);
    } else {
      favourites = favourites.concat([rowData.id]);
    }

    localStorage.setItem("current-tours-favourites", favourites.join(","));

    const newData = tableRef.current.state.data.map((item) => {
      if (item.id === rowData.id) {
        return {
          ...item,
          is_favourite: !item.is_favourite,
        };
      }
      return item;
    });

    settableData(newData.sort((a, b) => b.is_favourite - a.is_favourite));
  };

  const redirectView = (pathway, rowData, pathwayIndex) => {
    if (!rowData || !rowData.id) {
      console.warn('redirectView called with invalid rowData:', rowData);
      return;
    }

    const index = pathwayIndex ?? 0;
    const stop = rowData.pathway?.[index];
    let state;
    if (stop?.latitude != null && stop?.longitude != null) {
      state = { initialLat: stop.latitude, initialLng: stop.longitude };
    } else if (
      index === 0 &&
      rowData.origin_coordinates?.coordinates?.length >= 2
    ) {
      state = {
        initialLat: rowData.origin_coordinates.coordinates[1],
        initialLng: rowData.origin_coordinates.coordinates[0],
      };
    }

    if (pathwayIndex !== undefined && pathwayIndex !== null) {
      history.push({ pathname: `${mapPath}/${rowData.id}/${pathwayIndex}`, state });
      return;
    }

    history.push({ pathname: `${mapPath}/${rowData.id}`, state });
  };

  const handleUndo = (id) => {
    if (!confirm) return;
    confirm(
      () => {
        dispatch(undoRoute(id, t)).then(() => {
          loadFirstPage();
        });
      },
      {
        description: t("Are you sure you want to undo this route?"),
      }
    );
  };

  const handleEditRoute = (route) => {
    setSelectedRouteId(route.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRouteId(null);
  };

  const handleSaveRoute = async (updatedRoute) => {
    await dispatch(updateRoutePositions(updatedRoute.id, updatedRoute.pathway, t));
    handleCloseModal();
    loadFirstPage();
  };

  const handleDeviceInfo = (deviceInfo) => {
    setSelectedDeviceInfo(deviceInfo);
    setDeviceInfoModalOpen(true);
  };

  const handleCloseDeviceInfoModal = () => {
    setDeviceInfoModalOpen(false);
    setSelectedDeviceInfo(null);
  };

  const permissionBasedColumns = (user) => {
    const type = sortingType(user);

    if (routeType === 'current') {
      if (user?.account_permissions?.delivery_app_doc && columnsWithPDF) {
        return getColumns(
          columnsWithPDF(
            tableRef,
            features.favourites ? markFavourite : null,
            redirectView,
            t,
            features.undo ? handleUndo : null,
            type,
            user,
            features.reorder ? handleEditRoute : null,
            handleDeviceInfo
          ),
          t
        );
      }
      return getColumns(
        columnsConfig(
          tableRef,
          features.favourites ? markFavourite : null,
          redirectView,
          t,
          features.undo ? handleUndo : null,
          type,
          user,
          features.reorder ? handleEditRoute : null,
          handleDeviceInfo
        ),
        t
      );
    } else if (routeType === 'recent') {
      return getColumns(
        columnsConfig(
          tableRef,
          features.favourites ? markFavourite : null,
          redirectView,
          t,
          type,
          user,
          handleDeviceInfo
        ),
        t
      );
    } else if (routeType === 'archived') {
      return getColumns(
        columnsConfig(
          tableRef,
          redirectView,
          t,
          user,
          type,
          handleDeviceInfo
        ),
        t
      );
    }

    return getColumns(columnsConfig(tableRef, redirectView, t, user, type, handleDeviceInfo), t);
  };

  return (
    <>
      <Navbar />
      <ToursNavbar />
      <DarkLayout doublebar loading={loading}>
        {renderFilters && renderFilters()}
        <div style={{ paddingBottom: 48, position: "relative" }}>
          <MaterialTable
            tableRef={tableRef}
            data={mapTableData(tabledata)}
            localization={getLocalization(t)}
            columns={permissionBasedColumns(user)}
            options={{
              pageSize: 50,
              pageSizeOptions: [50, 100],
              sorting: false,
              paging: false,
              detailPanelColumnAlignment: "right",
              header: false,
              searchFieldAlignment: "left",
              showTitle: false,
              cellStyle: {
                color: "white",
                border: "none",
                font: "normal normal normal 18px/24px Roboto",
              },
              rowStyle: (rowData) => {
                if (rowData.tableData.id % 2 === 0) {
                  return { backgroundColor: " #1F1F1F " };
                } else {
                  return { backgroundColor: "#525252" };
                }
              },
            }}
            detailPanel={[
              {
                icon: () => (
                  <img
                    alt="icon"
                    src={arrowpanel}
                    style={{
                      width: "22px",
                      height: " 10px",
                      transform: " rotate(180deg)",
                    }}
                  />
                ),
                openIcon: () => (
                  <img
                    alt="icon"
                    src={arrowpanel}
                    style={{
                      width: "22px",
                      height: " 10px",
                    }}
                  />
                ),
                render: (rowData) => {
                  return (
                    <TableExpansionPanel
                      {...{
                        rowData,
                        scroll,
                        redirectView,
                        myDivToFocus,
                      }}
                    />
                  );
                },
              },
            ]}
          />
        </div>
        {pagination.hasNextPage && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <button
              onClick={loadNextPage}
              disabled={pagination.isLoadingMore}
              style={{
                backgroundColor: '#6F9CEB',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: pagination.isLoadingMore ? 'not-allowed' : 'pointer',
                opacity: pagination.isLoadingMore ? 0.6 : 1
              }}
            >
              {pagination.isLoadingMore ? t("Loading...") : t("Load More")}
            </button>
          </div>
        )}
      </DarkLayout>
      {features.reorder && (
        <ReorderStopsModal
          open={isModalOpen}
          onClose={handleCloseModal}
          routeId={selectedRouteId}
          onSave={handleSaveRoute}
        />
      )}
      <DriverDeviceInfoModal
        open={deviceInfoModalOpen}
        onClose={handleCloseDeviceInfoModal}
        deviceInfo={selectedDeviceInfo}
      />
    </>
  );
};

export default GenericRoutesList;
